import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import LinkOffIcon from '@mui/icons-material/LinkOff';

/**
 * @param {{
 *   variant: 'google-first' | 'password-first',
 *   email: string,
 *   onGoogleVerify?: () => void,
 *   onPasswordLink?: () => void,
 *   onCancel: () => void,
 *   submitting?: boolean,
 *   error?: string | null,
 *   children?: React.ReactNode,
 * }} props
 */
export default function AccountLinkingPanel({
  variant,
  email,
  onGoogleVerify,
  onPasswordLink,
  onCancel,
  submitting = false,
  error = null,
  children,
}) {
  const isGoogleFirst = variant === 'google-first';

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Alert severity="info" icon={<LinkOffIcon />} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Link your sign-in methods
        </Typography>
        {isGoogleFirst ? (
          <Typography variant="body2">
            <strong>{email}</strong> is registered with Google. Sign in with Google to verify it&apos;s
            you — we&apos;ll then add your password so you can use either method next time.
          </Typography>
        ) : (
          <Typography variant="body2">
            <strong>{email}</strong> already has a password. Enter your password below to verify your
            identity, then we&apos;ll link Google sign-in to the same account.
          </Typography>
        )}
      </Alert>

      {children}

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {isGoogleFirst ? (
          <Button
            type="button"
            fullWidth
            variant="contained"
            startIcon={<GoogleIcon />}
            disabled={submitting}
            onClick={onGoogleVerify}
          >
            Sign in with Google to link password
          </Button>
        ) : (
          <Button
            type="button"
            fullWidth
            variant="contained"
            disabled={submitting}
            onClick={onPasswordLink}
          >
            Verify password &amp; link Google
          </Button>
        )}
        <Button type="button" fullWidth variant="text" disabled={submitting} onClick={onCancel}>
          Cancel
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
